import React, { Component } from "react";
import { Query } from "@apollo/client/react/components";
import { graphql } from "@apollo/client/react/hoc";
import Loader from "react-loaders";

import { Button } from "../../../Button";
import { TierItem } from "../../../../typings/pricing";
import { SubscriptionContext } from "../../../../services/SubscriptionService";
import { CHANGE_USER_SUBSCRIPTION_PREVIEW } from "../../../../gql/queries";
import { CHANGE_USER_SUBSCRIPTION, UPDATE_USER_SPORT } from "../../../../gql/mutations";
import styles from "./styles.scss";
import AnalyticsService from "../../../../services/AnalyticsService";
import { responsiveImage } from "src/lib/responsive-image";

const UpArrowIcon = responsiveImage("/icon/up-arrow@3x.png", [50, 100, 150]);
const CreditCardIcon = responsiveImage("/icon/credit-card-icon@3x.png", [50, 100, 150]);

export interface UpgradeViewProps {
  userSubscription?: any;
  currentTier?: TierItem;
  selectedTier: TierItem;
  visible: boolean;
  toggleModal(): void;
  signedIn: boolean;
  changeUserSubscription(obj: any): Promise<void>;
  updateUserSport(obj: any): Promise<void>;
  sportList: string[];
}

export interface UpgradeViewState {
  loading: boolean;
  error?: string;
}

class UpgradeView extends Component<UpgradeViewProps, UpgradeViewState> {
  constructor(props: UpgradeViewProps) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  componentDidUpdate(prevProps: UpgradeViewProps) {
    if (prevProps.signedIn && !this.props.signedIn) {
      this.props.toggleModal();
    }
  }

  render() {
    const { toggleModal, selectedTier, currentTier: initialCurrentTier, userSubscription, sportList } = this.props;
    const { loading, error } = this.state;
    let currentTier = {
      title: "",
      durationTitle: "",
      price: 0,
    };
    if (initialCurrentTier) {
      currentTier = {
        title: initialCurrentTier.title,
        durationTitle: initialCurrentTier.durationTitle,
        price: initialCurrentTier.price,
      };
    }
    return (
      <SubscriptionContext.Consumer>
        {({ setSubscriptionLevel, subscriptionUpgradeSource }) => (
          <div className="rotoql-upgrade-view">
            <Query
              query={CHANGE_USER_SUBSCRIPTION_PREVIEW}
              variables={{
                currentUserSubscriptionId: userSubscription.id,
                targetSubscriptionName: selectedTier.key,
              }}
              notifyOnNetworkStatusChange
              errorPolicy="all"
            >
              {({ data }: any) => {
                // startingBalance: is how much credit they currently have in stripe
                // totalDue: is how much you either need to pay or how much you will get credited (if its negative)
                const totalDue = data?.stripeChangeUserSubscriptionPreview
                  ? `$${data.stripeChangeUserSubscriptionPreview.totalDue / 100}` // prettier-ignore
                  : "Loading...";

                return (
                  <React.Fragment>
                    <div className="rotoql-upgrade-view__header">Upgrade</div>
                    <div className="rotoql-upgrade-view__bullet-container">
                      <img
                        src={UpArrowIcon.src}
                        srcSet={UpArrowIcon.srcSet}
                        style={{ width: "50px" }}
                        alt="Down Arrow"
                        className="rotoql-upgrade-view__bullet-icon"
                      />
                      <div className="rotoql-upgrade-view__bullet-text">
                        You will be Upgraded from {currentTier.title} {currentTier.durationTitle} to{" "}
                        {selectedTier.title} {selectedTier.durationTitle} immediately.
                      </div>
                    </div>
                    <div className="rotoql-upgrade-view__bullet-container">
                      <img
                        src={CreditCardIcon.src}
                        srcSet={CreditCardIcon.srcSet}
                        style={{ width: "50px" }}
                        alt="Credit Card Icon"
                        className="rotoql-upgrade-view__bullet-icon"
                      />
                      <div className="rotoql-upgrade-view__bullet-text">Pay now: {totalDue}</div>
                    </div>
                    {error ? <div className="rotoql-upgrade-view__error-text">{error}</div> : null}
                    <div className="rotoql-upgrade-view__button-container">
                      <Button
                        className="rotoql-upgrade-view__button"
                        onClick={async () => {
                          const trackingProps = {
                            screen: "UpgradeView",
                            page: "Pricing",
                            tab: selectedTier.durationTitle,
                            subscriptionLevel: `${selectedTier.title} ${selectedTier.durationTitle}`,
                            sports: sportList.length ? sportList.toString() : "All",
                            upgradeSource: subscriptionUpgradeSource,
                          };
                          this.setState({ loading: true });
                          try {
                            const subRes: any = await this.props.changeUserSubscription({
                              variables: {
                                currentUserSubscriptionId: userSubscription.id,
                                targetSubscriptionName: selectedTier.key,
                                sports: selectedTier.maxSports !== "all" ? sportList : null,
                              },
                              errorPolicy: "all",
                            });
                            const { data: subData, errors: subErrors } = subRes;
                            if (subErrors || !subData?.stripeChangeUserSubscription) {
                              throw new Error("Failed to change subscription.");
                            }
                            const token = subData.stripeChangeUserSubscription.token;
                            await setSubscriptionLevel(token);
                            toggleModal();
                            this.setState({ loading: false });
                            AnalyticsService.track("Upgrade Subscription", {
                              ...trackingProps,
                              price: data?.stripeChangeUserSubscriptionPreview?.totalDue, // prettier-ignore
                              success: true,
                            });
                          } catch (e: any) {
                            this.setState({ loading: false, error: e.message });

                            console.log("Error upgrading subscription: ", e.message);
                            AnalyticsService.track("Upgrade Subscription", {
                              ...trackingProps,
                              success: false,
                            });
                          }
                        }}
                      >
                        {loading ? <Loader type="ball-pulse" active /> : "CONFIRM UPGRADE"}
                      </Button>
                      <Button
                        className="rotoql-upgrade-view__button rotoql-upgrade-view__button--cancel"
                        onClick={() => toggleModal()}
                      >
                        CANCEL
                      </Button>
                    </div>
                    <style jsx>{styles}</style>
                  </React.Fragment>
                );
              }}
            </Query>
          </div>
        )}
      </SubscriptionContext.Consumer>
    );
  }
}

export default graphql<any>(CHANGE_USER_SUBSCRIPTION, { name: "changeUserSubscription" })(
  graphql<any>(UPDATE_USER_SPORT, { name: "updateUserSport" })(UpgradeView) as any
);
